import React, { useEffect, useState, useRef } from 'react';
import { Check, ArrowDownLeft, ArrowUpRight, ShoppingBag } from 'lucide-react';

/* ── Animated Balance ─────────────────────── */
export function AnimatedBalance({ value, prefix = '' }) {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const start = prev.current;
    const end = value;
    const duration = 800;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(start + (end - start) * eased);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    prev.current = end;
  }, [value]);
  return <>{prefix}{display.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>;
}

/* ── Progress Bar ─────────────────────────── */
export function ProgressBar({ value, max, color = '#B03140' }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ height: 6, borderRadius: 3, background: 'rgba(176,49,64,0.1)', overflow: 'hidden' }}>
      <div style={{
        height: '100%', borderRadius: 3, background: color,
        width: `${pct}%`, animation: 'gdlProgressFill 0.6s ease forwards',
      }} />
    </div>
  );
}

/* ── Section Card ─────────────────────────── */
export function SectionCard({ title, action, onAction, children, style }) {
  return (
    <div style={{
      background: '#FFFFFF', borderRadius: 20,
      boxShadow: '0 2px 12px rgba(176,49,64,0.06)',
      padding: 20, animation: 'gdlFadeIn 0.3s ease', ...style,
    }}>
      {(title || action) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          {title && <div style={{ fontSize: 16, fontWeight: 700, color: '#2D0A10' }}>{title}</div>}
          {action && (
            <button onClick={onAction} style={{ background: 'none', border: 'none', color: '#B03140', fontSize: 13, fontWeight: 600, cursor: 'pointer' }} data-testid="section-action">
              {action}
            </button>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

/* ── Transaction Item ─────────────────────── */
export function TransactionItem({ tx }) {
  const isCredit = tx.amount >= 0;
  const isVendor = tx.type === 'vendor' || tx.type === 'bill';
  const iconBg = isVendor ? 'rgba(176,49,64,0.08)' : isCredit ? '#ECFDF5' : '#FEF2F2';
  const iconColor = isVendor ? '#B03140' : isCredit ? '#10B981' : '#EF4444';
  const Icon = isVendor ? ShoppingBag : isCredit ? ArrowDownLeft : ArrowUpRight;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid rgba(176,49,64,0.05)' }} data-testid={`tx-${tx.id}`}>
      <div style={{ width: 40, height: 40, borderRadius: 12, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={18} color={iconColor} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#2D0A10', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {tx.description || tx.type}
        </div>
        <div style={{ fontSize: 12, color: '#A07078' }}>{new Date(tx.created_at).toLocaleDateString()}</div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: isCredit ? '#10B981' : '#2D0A10' }}>
          {isCredit ? '+' : '-'}{'\u20A6'}{Math.abs(tx.amount).toLocaleString()}
        </div>
      </div>
    </div>
  );
}

/* ── Badge ────────────────────────────────── */
export function Badge({ variant = 'default', children }) {
  const styles = {
    success: { bg: '#ECFDF5', color: '#059669' },
    error: { bg: '#FEF2F2', color: '#DC2626' },
    warning: { bg: '#FFFBEB', color: '#D97706' },
    grey: { bg: 'rgba(176,49,64,0.06)', color: '#8E2233' },
    primary: { bg: 'rgba(176,49,64,0.1)', color: '#B03140' },
    default: { bg: 'rgba(176,49,64,0.06)', color: '#8E2233' },
  };
  const s = styles[variant] || styles.default;
  return (
    <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color }} data-testid="badge">
      {children}
    </span>
  );
}

/* ── PIN Pad ──────────────────────────────── */
export function PinPad({ length = 6, onComplete, title = 'Enter PIN' }) {
  const [digits, setDigits] = useState('');
  const handleDigit = (d) => {
    if (digits.length >= length) return;
    const next = digits + d;
    setDigits(next);
    if (next.length === length) setTimeout(() => onComplete(next), 200);
  };
  const handleDelete = () => setDigits(d => d.slice(0, -1));

  return (
    <div style={{ textAlign: 'center', padding: '24px 16px' }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#2D0A10', marginBottom: 24 }}>{title}</div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 32 }}>
        {Array.from({ length }).map((_, i) => (
          <div key={i} style={{
            width: 48, height: 56, borderRadius: 12,
            border: `2px solid ${i === digits.length ? '#B03140' : 'rgba(176,49,64,0.15)'}`,
            background: i < digits.length ? 'rgba(176,49,64,0.08)' : '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, fontWeight: 700, color: '#B03140',
            transition: 'all 0.15s ease',
          }}>
            {i < digits.length ? '\u2022' : ''}
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 72px)', gap: 8, justifyContent: 'center' }}>
        {[1,2,3,4,5,6,7,8,9,null,0,'del'].map((n, i) => (
          <button key={i}
            onClick={() => n === 'del' ? handleDelete() : n !== null && handleDigit(String(n))}
            disabled={n === null}
            style={{
              height: 56, borderRadius: 16, border: 'none', fontSize: 20, fontWeight: 600,
              background: n === null ? 'transparent' : 'rgba(176,49,64,0.06)',
              color: n === 'del' ? '#A07078' : '#2D0A10',
              cursor: n === null ? 'default' : 'pointer',
              transition: 'all 0.1s ease',
            }}
            data-testid={`pin-${n}`}
          >
            {n === 'del' ? '\u232B' : n === null ? '' : n}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Success Screen ───────────────────────── */
export function SuccessScreen({ title, subtitle, reference, buttonLabel = 'Done', onDone }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px', animation: 'gdlFadeIn 0.3s ease' }}>
      <div style={{
        width: 80, height: 80, borderRadius: '50%',
        background: 'linear-gradient(135deg, #B03140, #8E2233)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 24, animation: 'gdlBounceCheck 0.6s ease',
        boxShadow: '0 8px 24px rgba(176,49,64,0.3)',
      }}>
        <Check size={36} color="#fff" strokeWidth={3} />
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#2D0A10', marginBottom: 8 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 14, color: '#A07078', marginBottom: 16 }}>{subtitle}</div>}
      {reference && (
        <div style={{ display: 'inline-block', background: 'rgba(176,49,64,0.06)', borderRadius: 10, padding: '8px 16px', fontFamily: 'monospace', fontSize: 13, color: '#8E2233', marginBottom: 24 }}>
          Ref: {reference}
        </div>
      )}
      <div>
        <button onClick={onDone} data-testid="success-done-btn" style={{
          width: '100%', maxWidth: 280, padding: '14px 24px',
          background: '#B03140', color: '#fff',
          border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(176,49,64,0.25)',
        }}>
          {buttonLabel}
        </button>
      </div>
    </div>
  );
}

/* ── Empty State ──────────────────────────── */
export function EmptyState({ icon: Icon, title, body, action, onAction }) {
  return (
    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
      {Icon && <Icon size={48} color="rgba(176,49,64,0.2)" style={{ marginBottom: 16 }} />}
      <div style={{ fontSize: 16, fontWeight: 600, color: '#2D0A10', marginBottom: 4 }}>{title}</div>
      {body && <div style={{ fontSize: 14, color: '#A07078', maxWidth: 240, margin: '0 auto 16px' }}>{body}</div>}
      {action && (
        <button onClick={onAction} style={{
          padding: '10px 20px', background: '#B03140', color: '#fff',
          border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(176,49,64,0.2)',
        }}>
          {action}
        </button>
      )}
    </div>
  );
}
